import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Search, ArrowRight, Clapperboard, ShieldAlert, FileText, Plus, ArrowLeft, UserRound, Users, UserPlus, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { characterAvatar } from "../lib/characterAvatar";
import ProjectHeader from "../components/ProjectHeader";
import AddSceneDialog from "../components/AddSceneDialog";
import CreateCharacterDialog from "../components/CreateCharacterDialog";
import EmptyScriptOnboarding from "../components/EmptyScriptOnboarding";
import HMBackupBanner from "../components/HMBackupBanner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import usePolling from "../hooks/usePolling";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
// Iter-127 — shared episode-grouping helpers (ScenesHub uses the
// same module so the three surfaces stay byte-identical).
import {
    NO_EPISODE,
    episodeLabelFor,
    groupScenesByEpisode,
    isTvProject,
} from "../lib/episodeGroups";
import { sortCharacters } from "../lib/characters";
import { formatSceneNumber } from "../lib/scenes";

export default function OnSetMode() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();

    const [project, setProject] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [characters, setCharacters] = useState([]);
    const [sceneCharMap, setSceneCharMap] = useState({});
    const [charSceneCount, setCharSceneCount] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [addOpen, setAddOpen] = useState(false);
    // Cast-create state — same surfaces and permission gate as
    // AssignmentDialog and CastHub. Members without `can_create_characters`
    // see a "Request permission" affordance instead of the +Add button.
    const [createCastOpen, setCreateCastOpen] = useState(false);
    const [permissions, setPermissions] = useState({ can_create_characters: false });
    const [hasPendingRequest, setHasPendingRequest] = useState(false);
    const [requestingPerm, setRequestingPerm] = useState(false);
    const searchRef = useRef(null);

    const isOwner = project?.role === "owner";
    const isAdmin = project?.role === "admin";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    const canManage = (isOwner || isAdmin) && !isArchived;
    const canCreateCharacters =
        !isArchived &&
        (isOwner || isAdmin || permissions?.can_create_characters);
    // True when the user reached this page via the Cast hub's
    // "Open On Set mode" CTA. We surface a local back link in that
    // case so they can return to /cast with their lens + team filters
    // intact, instead of being forced back to the project root by the
    // referrer-guarded master back button.
    const fromCast = location.state?.from === "cast";

    // iter-52 #4: write the working lens to localStorage so SceneDetail's
    // "Back to scenes" returns to On Set when the user came in via On Set.
    useEffect(() => {
        try { window.localStorage.setItem("onsetflow:scenes-lens", "onset"); } catch { /* ignore */ }
    }, []);

    const reloadScenes = useCallback(async () => {
        try {
            // Iter-149.1 (P0) — no status filter. Both draft +
            // confirmed scenes must be reachable from On Set so
            // operators can shoot scenes immediately after parse
            // without having to run the optional review step.
            const { data: sc } = await api.get(`${apiBase}/${projectId}/scenes`);
            setScenes((prev) => {
                if (
                    prev.length === sc.length &&
                    prev.every(
                        (p, i) =>
                            p.id === sc[i]?.id &&
                            p.updated_at === sc[i]?.updated_at &&
                            p.shot_at === sc[i]?.shot_at,
                    )
                ) {
                    return prev;
                }
                return sc;
            });
        } catch (e) {
            setError(formatApiError(e));
        }
    }, [projectId, apiBase]);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const [{ data: p }, { data: sc }, { data: links }, { data: ch }, { data: perms }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`, { signal: ctrl.signal }),
                    api.get(`${apiBase}/${projectId}/scenes`, {
                        // Iter-149.1 (P0) — see reloadScenes note.
                        signal: ctrl.signal,
                    }),
                    api.get(`${apiBase}/${projectId}/scene-characters`, { signal: ctrl.signal }),
                    api.get(`${apiBase}/${projectId}/characters`, { signal: ctrl.signal }),
                    api.get(`${apiBase}/${projectId}/permissions/me`, { signal: ctrl.signal }),
                ]);
                setProject(p);
                setScenes(sc);
                setCharacters(ch);
                setPermissions(perms?.permissions || { can_create_characters: false });
                setHasPendingRequest(Boolean(perms?.has_pending_request));
                const map = {};
                // Reverse map: how many distinct scenes each character is
                // linked to. Powers the "in N scenes" hint on cast-result
                // pills so the user immediately sees the character's reach.
                const sceneCount = {};
                for (const l of links || []) {
                    if (!l?.scene_id || !l?.name) continue;
                    (map[l.scene_id] = map[l.scene_id] || []).push(l.name);
                    if (l.character_id) {
                        sceneCount[l.character_id] = (sceneCount[l.character_id] || 0) + 1;
                    }
                }
                setSceneCharMap(map);
                setCharSceneCount(sceneCount);
            } catch (e) {
                if (ctrl.signal.aborted) return;
                setError(formatApiError(e));
            } finally {
                if (!ctrl.signal.aborted) setLoading(false);
            }
        })();
        return () => ctrl.abort();
    }, [projectId, apiBase]);

    // Refetch scenes when returning from Scene Detail (e.g. after an omit or
    // reactivate) or when another tab dispatches a scene-updated event.
    // iter-74 PART 2 — also refetch on `state-entry-saved` so the
    // shot/dot indicator on each scene row turns dim the moment a
    // capture lands, without the user having to leave the list and
    // come back.
    useEffect(() => {
        const onSceneChanged = () => {
            reloadScenes();
        };
        const onPageShow = (e) => {
            if (e.persisted) reloadScenes();
        };
        window.addEventListener("onsetflow:scene-updated", onSceneChanged);
        window.addEventListener("onsetflow:state-entry-saved", onSceneChanged);
        window.addEventListener("pageshow", onPageShow);
        return () => {
            window.removeEventListener("onsetflow:scene-updated", onSceneChanged);
            window.removeEventListener("onsetflow:state-entry-saved", onSceneChanged);
            window.removeEventListener("pageshow", onPageShow);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // Iter-95 — background poll so scenes stay current when other
    // devices capture entries / mark scenes shot. Silent in-place
    // merge preserves scroll, focus, and dropdown state.
    usePolling(reloadScenes, { intervalMs: 12000 });

    const needle = q.trim().toLowerCase();

    const filtered = useMemo(() => {
        if (!needle) return scenes;
        return scenes.filter((s) => {
            const chars = sceneCharMap[s.id] || [];
            const hay = [s.scene_number, s.heading, s.int_ext, s.time_of_day, ...chars]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase());
            return hay.some((v) => v.includes(needle));
        });
    }, [needle, scenes, sceneCharMap]);

    // Iter-127 — group filtered scenes by episode so episodic
    // projects render the same accordion UX as the main Scenes page.
    // For pure-film projects (no explicit episode anywhere) we fall
    // through to the existing flat list. The grouping uses the SHARED
    // helper so OnSetMode + PrepMode + ScenesHub all sort identically.
    const groupedFiltered = useMemo(
        () => groupScenesByEpisode(filtered),
        [filtered],
    );
    const isTvMode = useMemo(
        () => isTvProject(groupScenesByEpisode(scenes)),
        [scenes],
    );
    // Iter-137 — Episode-group collapse state. Defaults to ALL COLLAPSED
    // regardless of how the user reaches OnSetMode (Overview tile,
    // Scenes "Open On Set mode" CTA, deep-link, etc.). The earlier
    // expanded-by-default behaviour created entry-state inconsistency:
    // soft-nav from Scenes (which collapses internally) preserved the
    // collapsed feel, but a fresh cold load from Overview opened every
    // episode at once, producing operational noise.
    //
    // Collapsed-by-default matches PrepMode + ScenesHub, gives the
    // same low-noise scanning surface across all navigation paths,
    // and a one-tap expand still reveals every scene.
    //
    // We seed the set lazily ONCE — as soon as the first non-empty
    // group list arrives — and never touch it again on subsequent
    // refreshes. User toggles always win; newly-added episodes that
    // appear AFTER the seed simply default to expanded (acceptable,
    // since the user is actively exploring at that point and a brand
    // new episode is rare).
    const [collapsedEps, setCollapsedEps] = useState(() => new Set());
    const seededRef = useRef(false);
    // Iter-155B.1 — when returning from a scene detail with
    // `expandEpisode` in nav state, expand that episode immediately
    // and scroll the previously-opened scene into view.
    const expandEpisodeFromNav = location.state?.expandEpisode || null;
    const scrollToSceneId = location.state?.scrollToSceneId || null;
    useEffect(() => {
        if (seededRef.current) return;
        if (!groupedFiltered || groupedFiltered.length === 0) return;
        const initial = new Set();
        for (const g of groupedFiltered) {
            if (expandEpisodeFromNav && g.key === expandEpisodeFromNav) continue;
            initial.add(g.key);
        }
        setCollapsedEps(initial);
        seededRef.current = true;
        if (scrollToSceneId) {
            setTimeout(() => {
                const el = document.querySelector(
                    `[data-testid="onset-scene-${scrollToSceneId}"]`,
                );
                if (el && typeof el.scrollIntoView === "function") {
                    el.scrollIntoView({ block: "center", behavior: "auto" });
                }
            }, 60);
        }
    }, [groupedFiltered, expandEpisodeFromNav, scrollToSceneId]);
    const toggleEpisode = (key) =>
        setCollapsedEps((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    // Cast match list — surfaced ABOVE the scene matches when the user
    // searches. Prevents the previous "search is scene-only" feel where
    // typing a character name produced empty results unless they had
    // already been attached to a scene.
    const filteredCharacters = useMemo(() => {
        if (!needle) return [];
        // Iter-129 — natural-numeric sort then filter.
        return sortCharacters(characters).filter((c) =>
            [c.name, c.actor, c.character_number]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase())
                .some((v) => v.includes(needle))
        );
    }, [needle, characters]);

    const requestCreatePermission = async () => {
        if (hasPendingRequest || requestingPerm) return;
        setRequestingPerm(true);
        try {
            await api.post(`${apiBase}/${projectId}/permission-requests`, {
                kind: "create_characters",
            });
            setHasPendingRequest(true);
            toast.success("Request sent to the project owner");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRequestingPerm(false);
        }
    };

    // Iter-127 — extracted scene-row render so the flat list AND the
    // grouped accordion produce identical markup.
    // Iter-155B.1 — accepts an `episodeKey` so the Back-from-detail
    // navigation can restore the right episode + scroll position.
    const renderSceneRow = (s, episodeKey) => {
        if (s.omitted) {
            // Iter-157P.18.2 — list views answer ONE question:
            // "is this scene omitted?". Amber = omitted regardless
            // of cause. Detailed explanation lives on Scene Detail.
            const byRevision = Boolean(s.omitted_by_revision_id);
            return (
                <li key={s.id}>
                    <Link
                        data-testid={`onset-scene-${s.id}`}
                        data-omitted="true"
                        data-omission-cause={byRevision ? "revision" : "script"}
                        to={`${routeBase}/${projectId}/scenes/${s.id}`}
                        state={{
                            returnTo: {
                                path: `${routeBase}/${projectId}/onset`,
                                label: "Back to On Set",
                                expandEpisode: episodeKey || null,
                                scrollToSceneId: s.id,
                            },
                        }}
                        title={
                            byRevision
                                ? "Omitted by approved script revision. Continuity preserved. Tap the scene for full details."
                                : "Marked omitted in the original script. Continuity preserved. Tap the scene for full details."
                        }
                        className="tap min-h-[64px] flex items-center gap-4 rounded-xl border border-dashed border-amber-400/60 bg-amber-500/10 px-4 py-4 hover:bg-amber-500/15"
                    >
                        <div className="mono text-xl w-12 text-center font-semibold text-amber-100">
                            {formatSceneNumber(s) || "—"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="inline-flex items-center gap-2">
                                <span
                                    data-testid={`onset-scene-${s.id}-omitted-badge`}
                                    className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full font-semibold bg-amber-500/30 text-amber-50 border border-amber-400/60"
                                >
                                    Omitted
                                </span>
                            </div>
                            {scene_description(s) ? (
                                <div className="text-sm mt-1 truncate line-through text-amber-50/90">
                                    {scene_description(s)}
                                </div>
                            ) : null}
                        </div>
                        <ArrowRight size={18} className="text-amber-200" />
                    </Link>
                </li>
            );
        }
        return (
            <li key={s.id}>
                <Link
                    data-testid={`onset-scene-${s.id}`}
                    data-shot={s.has_onset_entries ? "true" : "false"}
                    to={`${routeBase}/${projectId}/scenes/${s.id}`}
                    state={{
                        returnTo: {
                            path: `${routeBase}/${projectId}/onset`,
                            label: "Back to On Set",
                            expandEpisode: episodeKey || null,
                            scrollToSceneId: s.id,
                        },
                    }}
                    className={`tap min-h-[72px] flex items-center gap-4 rounded-xl border border-white/10 bg-card px-4 py-4 hover:bg-white/5 ${
                        s.has_onset_entries ? "opacity-50 hover:opacity-90" : ""
                    }`}
                >
                    <div className="mono text-xl text-white w-12 text-center font-semibold">
                        {formatSceneNumber(s) || "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-lg font-semibold truncate">
                                {s.heading}
                            </span>
                            {s.has_onset_entries ? (
                                <span
                                    data-testid={`onset-scene-shot-badge-${s.id}`}
                                    className="text-[9px] uppercase tracking-[0.2em] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                >
                                    Shot
                                </span>
                            ) : null}
                        </div>
                        {(s.int_ext || s.time_of_day) ? (
                            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
                                {[s.int_ext, s.time_of_day].filter(Boolean).join(" · ")}
                            </div>
                        ) : null}
                    </div>
                    <ArrowRight size={20} className="text-neutral-500" />
                </Link>
            </li>
        );
    };

    return (
        <div data-testid="onset-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle="On Set" />
            {/* Iter-158.13.2.20f.2 #5 — H&M-only backup warning banner. */}
            <HMBackupBanner
                projectId={projectId}
                scriptPresent={Array.isArray(scenes) && scenes.length > 0}
            />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                {fromCast ? (
                    <button
                        type="button"
                        data-testid="onset-back-to-cast"
                        onClick={() =>
                            navigate(`${routeBase}/${projectId}/cast`, {
                                state: { lens: "onset", team: location.state?.team },
                            })
                        }
                        className="tap inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white mb-4"
                    >
                        <ArrowLeft size={14} />
                        Back to Cast
                    </button>
                ) : null}

                <div className="mb-7 flex items-center gap-3">
                    <div
                        data-testid="onset-identity-icon"
                        className="h-12 w-12 rounded-lg bg-sky-500 text-white flex items-center justify-center shrink-0"
                    >
                        <Clapperboard size={24} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="font-display text-4xl font-black text-white leading-none">
                            On Set
                        </h1>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-sky-200/70 mt-1">
                            Primary
                        </p>
                    </div>
                    <Link
                        to={`${routeBase}/${projectId}/script`}
                        data-testid="onset-script-link"
                        title="Script"
                        className="tap min-tap inline-flex items-center justify-center rounded-md h-11 w-11 text-neutral-300 hover:text-white hover:bg-white/5"
                    >
                        <FileText size={20} />
                    </Link>
                </div>

                {error ? (
                    <p data-testid="onset-error" className="text-sm text-red-400 mb-4">
                        {error}
                    </p>
                ) : null}

                <div className="sticky top-14 z-40 py-2 bg-background/90 backdrop-blur-xl mb-5">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search
                                size={18}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
                            />
                            <Input
                                ref={searchRef}
                                data-testid="onset-search"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search scenes"
                                className="h-12 pl-11 text-base bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                            />
                        </div>
                        {canManage ? (
                            <Button
                                data-testid="onset-add-scene-btn"
                                type="button"
                                onClick={() => setAddOpen(true)}
                                className="h-12 w-12 px-0 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shrink-0"
                                title="Add scene"
                                aria-label="Add scene"
                            >
                                <Plus size={20} />
                            </Button>
                        ) : null}
                        {/* Add cast — same permission gate as
                            CastHub + AssignmentDialog. Owners and
                            permitted members get a one-tap +User button;
                            unpermitted members see the request CTA below
                            (in the cast-results section header) so this
                            row stays uncluttered. */}
                        {canCreateCharacters ? (
                            <Button
                                data-testid="onset-add-cast-btn"
                                type="button"
                                variant="secondary"
                                onClick={() => setCreateCastOpen(true)}
                                className="h-12 w-12 px-0 shrink-0 border border-white/10 hover:bg-white/10"
                                title="Add cast member"
                                aria-label="Add cast member"
                            >
                                <UserPlus size={20} />
                            </Button>
                        ) : null}
                    </div>
                </div>

                {loading ? (
                    <ul className="space-y-2" data-testid="onset-loading">
                        {[0, 1, 2, 3].map((i) => (
                            <li
                                key={i}
                                className="h-16 rounded-xl border border-white/10 bg-card animate-pulse"
                            />
                        ))}
                    </ul>
                ) : (
                    <>
                        {/* Cast match section — only renders when the user is
                            actively searching AND at least one character
                            matches. Tapping a card jumps to the Cast hub
                            (with the On Set lens preserved via
                            localStorage) where the user can see actor,
                            number, team, and edit. */}
                        {needle && filteredCharacters.length > 0 ? (
                            <section
                                data-testid="onset-cast-results"
                                className="mb-5"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h2 className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-semibold">
                                        Cast · {filteredCharacters.length} match
                                        {filteredCharacters.length === 1 ? "" : "es"}
                                    </h2>
                                    {/* Quick-create affordance shown
                                        right where the user is looking
                                        when they fail to find someone.
                                        Falls back to a request-permission
                                        link when the user lacks the cap. */}
                                    {canCreateCharacters ? (
                                        <button
                                            type="button"
                                            data-testid="onset-cast-new-inline"
                                            onClick={() => setCreateCastOpen(true)}
                                            className="tap text-xs text-neutral-300 hover:text-white inline-flex items-center gap-1"
                                        >
                                            <UserPlus size={12} /> New character
                                        </button>
                                    ) : hasPendingRequest ? (
                                        <span
                                            data-testid="onset-cast-perm-pending"
                                            className="text-[11px] text-amber-300 inline-flex items-center gap-1"
                                        >
                                            <Clock size={11} /> Permission pending
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid="onset-cast-request-perm"
                                            onClick={requestCreatePermission}
                                            disabled={requestingPerm}
                                            className="tap text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1"
                                        >
                                            <ShieldAlert size={11} />
                                            {requestingPerm ? "Sending…" : "Request permission"}
                                        </button>
                                    )}
                                </div>
                                <ul className="space-y-2">
                                    {filteredCharacters.map((c) => {
                                        const team = c.team || "main";
                                        const sceneCount = charSceneCount[c.id] || 0;
                                        return (
                                            <li key={c.id}>
                                                <Link
                                                    data-testid={`onset-cast-${c.id}`}
                                                    to={`${routeBase}/${projectId}/characters/${c.id}`}
                                                    state={{
                                                        lens: "onset",
                                                        returnTo: {
                                                            path: `${routeBase}/${projectId}/onset`,
                                                            label: "Back to On Set",
                                                        },
                                                    }}
                                                    className="tap min-h-[64px] flex items-center gap-3 rounded-xl border border-white/10 bg-card px-4 py-3 hover:bg-white/5"
                                                >
                                                    <div
                                                        data-testid={`onset-cast-avatar-${c.id}`}
                                                        className={`h-10 w-10 shrink-0 rounded-full bg-secondary text-white font-display font-bold flex items-center justify-center ${
                                                            String(c.character_number || "").trim().length >= 3
                                                                ? "text-sm"
                                                                : "text-base"
                                                        }`}
                                                    >
                                                        {characterAvatar(c)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-white font-semibold truncate">
                                                            {c.name}
                                                        </div>
                                                        <div className="text-[11px] text-neutral-500 mt-0.5 truncate">
                                                            {c.actor ? `${c.actor} · ` : ""}
                                                            {sceneCount > 0
                                                                ? `in ${sceneCount} scene${sceneCount === 1 ? "" : "s"}`
                                                                : "no scenes yet"}
                                                        </div>
                                                    </div>
                                                    <span
                                                        className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] border ${
                                                            team === "main"
                                                                ? "bg-primary/15 border-primary/40 text-primary"
                                                                : "bg-amber-500/10 border-amber-500/30 text-amber-200"
                                                        }`}
                                                    >
                                                        {team === "main" ? (
                                                            <UserRound size={11} />
                                                        ) : (
                                                            <Users size={11} />
                                                        )}
                                                        {team === "main" ? "Main" : "Crowd"}
                                                    </span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        ) : null}

                        {scenes.length === 0 ? (
                            <EmptyScriptOnboarding
                                projectId={projectId}
                                variant="scenes"
                                role={project?.role}
                                canManageScript={project?.can_manage_script}
                            />
                        ) : filtered.length === 0 && filteredCharacters.length === 0 ? (
                            <div
                                data-testid="onset-search-empty"
                                className="rounded-xl border border-dashed border-white/10 p-6 text-center"
                            >
                                <p className="text-sm text-neutral-400">
                                    No scenes or cast match &quot;{q}&quot;.
                                </p>
                                {/* Quick-create lifeline. The user just
                                    confirmed the character isn't in the
                                    list — give them the action that
                                    fixes the gap right here. */}
                                {canCreateCharacters ? (
                                    <button
                                        type="button"
                                        data-testid="onset-empty-add-cast"
                                        onClick={() => setCreateCastOpen(true)}
                                        className="tap mt-3 inline-flex items-center gap-1.5 rounded-md px-3 h-9 bg-primary text-primary-foreground text-xs font-semibold"
                                    >
                                        <UserPlus size={13} />
                                        Add &ldquo;{q}&rdquo; to cast
                                    </button>
                                ) : null}
                            </div>
                        ) : filtered.length === 0 ? (
                            // Cast matched but no scenes — keep the message
                            // useful instead of contradicting the cast list
                            // we just rendered above.
                            <p
                                data-testid="onset-search-empty-scenes"
                                className="text-sm text-neutral-500 text-center py-4"
                            >
                                No scenes match &quot;{q}&quot;.
                            </p>
                        ) : (
                            <>
                                {needle && filteredCharacters.length > 0 ? (
                                    <h2
                                        data-testid="onset-scenes-heading"
                                        className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-semibold mb-2"
                                    >
                                        Scenes · {filtered.length} match
                                        {filtered.length === 1 ? "" : "es"}
                                    </h2>
                                ) : null}
                                <ul data-testid="onset-scenes-list" className="space-y-2">
                                    {isTvMode ? (
                                        // Iter-127 — episode-accordion render. Mirrors
                                        // the main ScenesHub UX so On Set is no longer
                                        // capped to "App 1" for episodic projects.
                                        // Iter-157P.17 — omitted scenes render INLINE
                                        // in scene-number order (P.16 collapsed-bottom
                                        // layout broke production discoverability —
                                        // producers looking for Scene 42 expect it
                                        // between 41 and 43). Visual cause distinction
                                        // is preserved on each row.
                                        groupedFiltered.map((g) => {
                                            if (g.scenes.length === 0) return null;
                                            const activeCount = g.scenes.filter((s) => !s.omitted).length;
                                            const omittedCount = g.scenes.length - activeCount;
                                            const isCollapsed = collapsedEps.has(g.key);
                                            const label = episodeLabelFor(g.key);
                                            const groupTestId =
                                                g.key === NO_EPISODE
                                                    ? "onset-episode-none"
                                                    : `onset-episode-${g.key}`;
                                            return (
                                                <li key={g.key}>
                                                    <button
                                                        type="button"
                                                        data-testid={`${groupTestId}-toggle`}
                                                        onClick={() => toggleEpisode(g.key)}
                                                        aria-expanded={!isCollapsed}
                                                        className="tap w-full flex items-center justify-between gap-3 px-3 h-11 rounded-lg bg-secondary/60 border border-white/10 text-left hover:bg-white/10"
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <ChevronDown
                                                                size={14}
                                                                className={`text-neutral-400 transition-transform ${
                                                                    isCollapsed ? "-rotate-90" : ""
                                                                }`}
                                                            />
                                                            <span className="font-display text-sm font-bold text-white">
                                                                {label}
                                                            </span>
                                                        </span>
                                                        <span
                                                            data-testid={`${groupTestId}-counts`}
                                                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-400"
                                                        >
                                                            {activeCount} scene
                                                            {activeCount === 1 ? "" : "s"}
                                                            {omittedCount > 0 ? (
                                                                <span className="ml-2 text-neutral-500">
                                                                    · {omittedCount} omitted
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    </button>
                                                    {!isCollapsed ? (
                                                        <ul
                                                            data-testid={`${groupTestId}-list`}
                                                            className="mt-2 space-y-2"
                                                        >
                                                            {g.scenes.map((s) => renderSceneRow(s, g.key))}
                                                        </ul>
                                                    ) : null}
                                                </li>
                                            );
                                        })
                                    ) : (
                                        filtered.map((s) => renderSceneRow(s))
                                    )}
                                </ul>
                            </>
                        )}
                    </>
                )}
            </main>

            {canManage ? (
                <AddSceneDialog
                    projectId={projectId}
                    existingScenes={scenes}
                    open={addOpen}
                    onClose={() => setAddOpen(false)}
                    onCreated={(newScene) =>
                        setScenes((prev) => {
                            if (prev.some((p) => p.id === newScene.id)) return prev;
                            return [...prev, newScene].sort(
                                (a, b) => (a.order_index || 0) - (b.order_index || 0),
                            );
                        })
                    }
                />
            ) : null}

            {/* Reusable cast-create dialog, same component CastHub uses.
                Permission gating happens at the trigger level — this
                dialog is only mounted when the user already has the cap. */}
            {canCreateCharacters ? (
                <CreateCharacterDialog
                    open={createCastOpen}
                    projectId={projectId}
                    onClose={() => setCreateCastOpen(false)}
                    onCreated={(data) => setCharacters((list) => [...list, data])}
                />
            ) : null}
        </div>
    );
}

// Render the non-heading portion of an omitted scene so numbering context + original heading is preserved for the reader.
function scene_description(s) {
    if (!s) return "";
    const h = s.heading || "";
    if (h.toUpperCase().trim() === "OMITTED") return "";
    return h;
}
